import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "next-themes";
import { USES_HASH_ROUTER } from "@/lib/router-mode";
import ErrorBoundary from "@/components/ErrorBoundary";

import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import FlightInit from "@/pages/FlightInit";
import LiveTracking from "@/pages/LiveTracking";
import LiveMap from "@/pages/LiveMap";
import Logbook from "@/pages/Logbook";
import Fpls from "@/pages/Fpls";
import Audio from "@/pages/Audio";
import Sounds from "@/pages/Sounds";
import Settings from "@/pages/Settings";
import WhatsNew from "@/pages/WhatsNew";
import Dispatch from "@/pages/Dispatch";
import BetaTest from "@/pages/BetaTest";
import BetaReports from "@/pages/BetaReports";
import BetaApplications from "@/pages/BetaApplications";
import Apply from "@/pages/Apply";

// In the Electron desktop shell we're loaded from file://, where BrowserRouter's
// history API breaks. HashRouter works everywhere. On the web (acars.flyvac.org)
// we keep BrowserRouter for clean URLs. Decided in router-mode.js, which the
// API's 401 handler also reads, so the two cannot disagree.
const Router = USES_HASH_ROUTER ? HashRouter : BrowserRouter;

function Guard({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Toasts follow the theme. The old fixed navy and Public Sans were left over
// from the pre-Air-Canada palette and matched neither theme.
function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return (
    <Toaster
      theme={resolvedTheme === "light" ? "light" : "dark"}
      position="top-right"
      toastOptions={{
        style: {
          background: "hsl(var(--card))",
          border: "1px solid hsl(var(--border))",
          color: "hsl(var(--foreground))",
          fontFamily: "Manrope, sans-serif",
        },
      }}
    />
  );
}

export default function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      themes={["dark", "light"]}
      storageKey="vacars-theme"
      disableTransitionOnChange
    >
      <ErrorBoundary>
        <AuthProvider>
          <Router>
            <ThemedToaster />
            <Routes>
              <Route path="/login" element={<Login />} />
              {/* Public: an applicant has no session in a closed beta. */}
              <Route path="/apply" element={<Apply />} />
              <Route element={<Guard><AppLayout /></Guard>}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/init" element={<FlightInit />} />
                <Route path="/tracking" element={<LiveTracking />} />
                <Route path="/livemap" element={<LiveMap />} />
                <Route path="/logbook" element={<Logbook />} />
                <Route path="/fpls" element={<Fpls />} />
                <Route path="/fltops" element={<Navigate to="/fpls" replace />} />
                <Route path="/dispatch" element={<Dispatch />} />
                <Route path="/audio" element={<Audio />} />
                <Route path="/admin/sounds" element={<Sounds />} />
                <Route path="/beta" element={<BetaTest />} />
                <Route path="/admin/beta" element={<BetaReports />} />
                <Route path="/admin/beta-applications" element={<BetaApplications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/whats-new" element={<WhatsNew />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
